/**
 * RecoverySettingsPage
 *
 * Admin-UI für das Abandonment-Recovery-System (neue Kunden, die
 * den Shop angesehen, aber nichts gekauft haben).
 *
 * Backend:
 *   GET  /api/admin/recovery-settings
 *   PUT  /api/admin/recovery-settings
 *   POST /api/admin/recovery/run-now
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Gift, Save, Play, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const DEFAULTS = {
  enabled: true,
  lead_time_days: 28,
  discount_percentage: 10,
  coupon_valid_days: 30,
};

export default function RecoverySettingsPage() {
  const [settings, setSettings] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/recovery-settings`, auth());
      setSettings({ ...DEFAULTS, ...(data || {}) });
    } catch (e) {
      toast.error('Fehler beim Laden: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: !!settings.enabled,
        lead_time_days: parseInt(settings.lead_time_days, 10) || 28,
        discount_percentage: parseInt(settings.discount_percentage, 10) || 10,
        coupon_valid_days: parseInt(settings.coupon_valid_days, 10) || 30,
      };
      await axios.put(`${API}/admin/recovery-settings`, payload, auth());
      setSettings({ ...settings, ...payload });
      toast.success('Einstellungen gespeichert');
    } catch (e) {
      toast.error('Fehler: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    if (!window.confirm(
      'Recovery-Erinnerungen jetzt manuell auslösen?\n\n' +
      'Alle Kunden, die seit ' + settings.lead_time_days + ' Tagen ' +
      'den Shop angeschaut, aber nichts gekauft haben, erhalten ' +
      'einen Rabattcode von ' + settings.discount_percentage + '%.'
    )) return;
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/admin/recovery/run-now`, {}, auth());
      const sent = data?.sent ?? 0;
      const skipped = data?.skipped ?? 0;
      toast.success(`Recovery-Lauf abgeschlossen — ${sent} versendet, ${skipped} übersprungen`);
    } catch (e) {
      toast.error('Fehler: ' + (e.response?.data?.detail || e.message));
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-gray-500" data-testid="recovery-settings-loading">
        Lade Recovery-Einstellungen …
      </div>
    );
  }

  return (
    <div className="p-8 max-w-3xl" data-testid="recovery-settings-page">
      <div className="flex items-center gap-2 mb-6">
        <Gift className="h-6 w-6 text-pink-600" />
        <h1 className="text-2xl font-bold">Abandonment-Recovery</h1>
      </div>

      <p className="text-sm text-gray-600 mb-6">
        Sendet automatisch einen <strong>Rabattcode</strong> an Kunden, die den
        Shop angesehen, aber noch nichts gekauft haben — eine elegante Methode,
        unentschlossene Neukunden zurückzuholen.
      </p>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">Einstellungen</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between rounded-lg border p-3 bg-gray-50">
            <div>
              <Label className="text-sm font-medium">System aktiv</Label>
              <p className="text-xs text-gray-500 mt-1">
                Schaltet den automatischen Cron-Job (täglich 10:15) ein bzw. aus.
              </p>
            </div>
            <Switch
              checked={!!settings.enabled}
              onCheckedChange={(v) => setSettings({ ...settings, enabled: v })}
              data-testid="recovery-enabled-switch"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Inaktivitäts-Schwelle</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  max="180"
                  value={settings.lead_time_days}
                  onChange={(e) => setSettings({ ...settings, lead_time_days: e.target.value })}
                  data-testid="recovery-lead-input"
                />
                <span className="text-sm text-gray-500 whitespace-nowrap">Tage</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Empfohlen: 28 Tage</p>
            </div>

            <div>
              <Label>Rabatt</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  max="50"
                  value={settings.discount_percentage}
                  onChange={(e) => setSettings({ ...settings, discount_percentage: e.target.value })}
                  data-testid="recovery-discount-input"
                />
                <span className="text-sm text-gray-500">%</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Empfohlen: 10 % — saisonal bis 15 %</p>
            </div>

            <div>
              <Label>Gültigkeit Code</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  max="180"
                  value={settings.coupon_valid_days}
                  onChange={(e) => setSettings({ ...settings, coupon_valid_days: e.target.value })}
                  data-testid="recovery-validity-input"
                />
                <span className="text-sm text-gray-500 whitespace-nowrap">Tage</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Empfohlen: 30 Tage</p>
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button onClick={save} disabled={saving} data-testid="recovery-save-btn">
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Speichere …' : 'Speichern'}
            </Button>
            <Button
              onClick={runNow}
              disabled={running || !settings.enabled}
              variant="outline"
              data-testid="recovery-run-now-btn"
            >
              <Play className="h-4 w-4 mr-2" />
              {running ? 'Läuft …' : 'Jetzt manuell auslösen'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="p-4 bg-amber-50 border border-amber-200 rounded">
        <div className="flex gap-2">
          <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <p className="font-semibold mb-1">So funktioniert das System</p>
            <p>
              Jeden Tag um <strong>10:15</strong> prüft das System, welche Neukunden
              den Shop seit mindestens <strong>{settings.lead_time_days} Tagen </strong>
              besucht, aber bislang noch nichts gekauft haben. Diese Kunden erhalten
              automatisch einen individuellen Rabattcode von <strong>{settings.discount_percentage} % </strong>
              per E-Mail (und WhatsApp, falls aktiviert). Der Code ist <strong>{settings.coupon_valid_days} Tage </strong>
              gültig, einmalig einlösbar und nur für den jeweiligen Kunden personalisiert.
            </p>
            <p className="mt-2">
              Jeder Kunde erhält pro Abandonment-Event maximal einen Code — die Kommunikation
              wird intern protokolliert und kann nicht doppelt versendet werden.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
